export const TV_CONTROLLER_GROUP_COLLECTION = 'tvControllerGroups';
// The whole tree lives in one doc so reorders and moves are a single write.
export const TV_CONTROLLER_GROUP_TREE_DOC = 'tree';

// Firestore caps nesting at 20 levels; each tree level costs two (array + map).
export const MAX_GROUP_DEPTH = 4;

export const GROUP_CODE_PREFIX = 'ZV_';
// Codes rotate daily: the hashed suffix is the venue date, which turns over
// at 3 AM local time instead of midnight.
export const GROUP_CODE_TIME_ZONE = 'America/New_York';
export const GROUP_CODE_ROTATE_HOUR = 3;

export type TvControllerGroup = {
  id: string;
  name: string;
  /** QR code payload (see `groupCode`). Parent (top-level) groups only. The
   *  mobile app pairs by matching this stored value and unpairs when it
   *  changes; the monitoring-agent rewrites it daily at 3 AM. */
  code?: string;
  /** Tag stamped on member TVs (`tvControllers.tags`). Null = no TVs of its
   *  own; the group only holds subgroups. */
  tag: string | null;
  /** `channelGuides` doc assigned to the whole group (parent groups only) and
   *  loaded on all its TVs; the mobile app lists its channels after scanning
   *  the group's QR code. Null/absent = none assigned. */
  channelGuideId?: string | null;
  children: TvControllerGroup[];
};

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

/** Standard CRC-32 (IEEE 802.3, same as zip/PNG) over the UTF-8 bytes. */
export function crc32(text: string): number {
  let crc = 0xffffffff;
  new TextEncoder().encode(text).forEach((byte) => {
    crc = CRC_TABLE[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  });
  return (crc ^ 0xffffffff) >>> 0;
}

/** `YYYYMMDD` of the venue day at `now`; before 3 AM it is still yesterday. */
export function dailySuffix(now: Date = new Date()): string {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: GROUP_CODE_TIME_ZONE,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      hourCycle: 'h23',
    })
      .formatToParts(now)
      .map((part) => [part.type, Number(part.value)])
  );
  const day = new Date(
    Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day - (parts.hour < GROUP_CODE_ROTATE_HOUR ? 1 : 0)
    )
  );
  return day.toISOString().slice(0, 10).replace(/-/g, '');
}

/** QR code payload of a parent group: `ZV_` + CRC32 of name + daily suffix.
 *  Changes every day at 3 AM (venue time) and on rename. Mirrored in the
 *  monitoring-agent's group-code-rotation.service.ts — keep them in sync. */
export function groupCode(name: string, suffix: string = dailySuffix()): string {
  return (
    GROUP_CODE_PREFIX +
    crc32(name.trim() + suffix).toString(16).toUpperCase().padStart(8, '0')
  );
}

export function flattenGroups(nodes: TvControllerGroup[]): TvControllerGroup[] {
  return nodes.flatMap((node) => [node, ...flattenGroups(node.children)]);
}

/** Levels in the subtree rooted at `node` (a childless group is 1). */
export function subtreeHeight(node: TvControllerGroup): number {
  return 1 + Math.max(0, ...node.children.map(subtreeHeight));
}

/** 1-based depth of the group with `id`, or 0 when it is not in the tree. */
export function groupDepth(
  nodes: TvControllerGroup[],
  id: string,
  depth = 1
): number {
  for (const node of nodes) {
    if (node.id === id) return depth;
    const found = groupDepth(node.children, id, depth + 1);
    if (found) return found;
  }
  return 0;
}

/** Return a copy of the tree with `update` applied to the group with `id`. */
export function updateGroup(
  nodes: TvControllerGroup[],
  id: string,
  update: (node: TvControllerGroup) => TvControllerGroup
): TvControllerGroup[] {
  return nodes.map((node) =>
    node.id === id
      ? update(node)
      : { ...node, children: updateGroup(node.children, id, update) }
  );
}

export function removeGroup(
  nodes: TvControllerGroup[],
  id: string
): TvControllerGroup[] {
  return nodes
    .filter((node) => node.id !== id)
    .map((node) => ({ ...node, children: removeGroup(node.children, id) }));
}

/** Append `group` under `parentId` (null = top level). */
export function insertGroup(
  nodes: TvControllerGroup[],
  parentId: string | null,
  group: TvControllerGroup
): TvControllerGroup[] {
  if (!parentId) return [...nodes, group];
  return updateGroup(nodes, parentId, (parent) => ({
    ...parent,
    children: [...parent.children, group],
  }));
}

/** Swap the group with its previous (-1) or next (+1) sibling. */
export function shiftGroup(
  nodes: TvControllerGroup[],
  id: string,
  direction: -1 | 1
): TvControllerGroup[] {
  const index = nodes.findIndex((node) => node.id === id);
  if (index === -1) {
    return nodes.map((node) => ({
      ...node,
      children: shiftGroup(node.children, id, direction),
    }));
  }
  const target = index + direction;
  if (target < 0 || target >= nodes.length) return nodes;
  const next = [...nodes];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

/** Codes and channel guides belong to parent groups only: top-level nodes get
 *  their code (re)derived from the name and today's suffix, subgroups lose both.
 *  Applied on every save so a move or rename can never leave a code on a
 *  subgroup. */
export function normalizeGroups(nodes: TvControllerGroup[]): TvControllerGroup[] {
  const strip = (node: TvControllerGroup): TvControllerGroup => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { code, channelGuideId, ...rest } = node;
    return { ...rest, children: node.children.map(strip) };
  };
  return nodes.map((node) => ({
    ...node,
    code: groupCode(node.name),
    channelGuideId: node.channelGuideId ?? null,
    children: node.children.map(strip),
  }));
}

/** Every tag under (and including) `node`: the TVs of a group and its subgroups. */
export function subtreeTags(node: TvControllerGroup): string[] {
  return flattenGroups([node]).flatMap((group) => (group.tag ? [group.tag] : []));
}
